let Lenis;
const { scrollMotion } = await import(globalThis.MeriAssets?.scrollMotion || './scroll-motion.js');

const reduced = matchMedia('(prefers-reduced-motion: reduce)');
// Devices that report a mouse or trackpad build the engine up front; touch-first
// devices (phones, and touchscreen laptops that report no fine pointer) build it
// on their first wheel, so phones never load the library.
const desktop = matchMedia('(any-hover: hover), (any-pointer: fine)');
// lerp: share of the remaining distance covered per frame; wheel: distance per notch.
const feels = { buffered: { lerp: 0.07, wheel: 0.85 }, soft: { lerp: 0.055, wheel: 0.75 } };
const feel = feels[document.documentElement.dataset.pageScroll] || feels.buffered;
// Trackpads already carry system inertia; they get a light touch at their native distance.
const trackpadLerp = Math.min(0.2, feel.lerp * 2.2);
let generation = 0;
let engine;
let unsubscribe;
let direction = 0;
let wheelSeen = false;
let unavailable = false;
let building = false;
let pending = 0;
let lastWheel = 0;
let trackpadScore = 0;
// 2.9.9 (Owner): a description window (help-window.js) scrolls its own text under the wheel; the page's glide used to
// take that wheel, so the page moved and the window closed.
const protectedSelector =
  'dialog, [role="dialog"], iframe, textarea, select, meri-help-window, .site-menu[open]:not([data-desktop="true"]) .site-menu__links';
// 2.9.1 (Owner, second round): a 3D model or a video keeps the wheel only while it plays. A model's preview, a paused
// model and a paused video scroll with the page like a picture, so the page's glide no longer stops over them.
function holdsWheel(node) {
  if (node.matches?.(protectedSelector)) return true;
  if (node.localName === 'meri-model-media') return node.hasAttribute('data-model-playing');
  if (node.localName === 'model-viewer') {
    const model = node.closest('meri-model-media');
    return model ? model.hasAttribute('data-model-playing') : true;
  }
  if (node.localName === 'video') return !node.paused && !node.ended;
  return false;
}
function settle() {
  if (engine?.isScrolling === 'smooth') engine.scrollTo(engine.actualScroll, { immediate: true, force: true });
}
function ownsWheel(event, deltaX, deltaY) {
  return (
    event.type === 'wheel' &&
    !event.ctrlKey &&
    !event.shiftKey &&
    Math.abs(deltaX) <= Math.abs(deltaY) &&
    !document.querySelector('dialog[open]') &&
    !event.defaultPrevented &&
    !event.composedPath().some(holdsWheel)
  );
}
// Mouse wheels send large stepped deltas; trackpads send a dense stream of small or fractional ones.
function trackpad(event) {
  const now = performance.now();
  const dense = now - lastWheel < 40;
  lastWheel = now;
  const pixels = event.deltaMode === 0;
  const smooth = pixels && ((Math.abs(event.deltaY) < 40 && dense) || !Number.isInteger(event.deltaY));
  trackpadScore = Math.max(-3, Math.min(3, trackpadScore + (smooth || event.deltaX ? 1 : -1)));
  return trackpadScore > 0;
}
async function sync() {
  const sequence = ++generation;
  settle();
  unsubscribe?.();
  unsubscribe = null;
  engine?.destroy();
  engine = null;
  if (reduced.matches || document.hidden || !(desktop.matches || wheelSeen)) {
    pending = 0;
    return;
  }
  building = true;
  try {
    Lenis ||= (await import(globalThis.MeriAssets?.scrollLibrary || './lenis.js')).default;
  } catch {
    // Without the library the page keeps native scrolling; release any held wheel distance.
    if (pending) scrollBy(0, pending);
    pending = 0;
    unavailable = true;
    return;
  } finally {
    building = false;
  }
  if (sequence !== generation) return;
  engine = new Lenis({
    autoRaf: false,
    autoToggle: false,
    smoothWheel: true,
    syncTouch: false,
    lerp: feel.lerp,
    wheelMultiplier: 1,
    anchors: false,
    stopInertiaOnNavigate: true,
    virtualScroll: (data) => {
      const { event, deltaY, deltaX } = data;
      if (!ownsWheel(event, deltaX, deltaY)) {
        settle();
        return false;
      }
      const next = Math.sign(deltaY);
      // A reversal starts from where the page is, never after finishing the old target.
      if (next && direction && next !== direction) settle();
      direction = next;
      if (trackpad(event)) engine.options.lerp = trackpadLerp;
      else {
        engine.options.lerp = feel.lerp;
        data.deltaY *= feel.wheel;
      }
      // The shared scheduler sleeps at rest; exclude that idle time from the next animation frame.
      if (engine.isScrolling !== 'smooth') engine.raf(performance.now());
      scrollMotion.schedule();
      return true;
    },
  });
  // 2.8.7 (Owner): fingers scroll natively (syncTouch is off), but Lenis also listens to touches without the passive
  // flag, so on touchscreen computers every swipe first waited for the page's scripts and a busy moment held it. Only
  // the wheel is buffered; a touch during a glide still stops it (pointerdown settles it below).
  const virtual = engine.virtualScroll;
  for (const [type, listener] of [
    ['touchstart', virtual?.onTouchStart],
    ['touchmove', virtual?.onTouchMove],
    ['touchend', virtual?.onTouchEnd],
  ])
    if (listener) virtual.element.removeEventListener(type, listener, { passive: false });
  unsubscribe = scrollMotion.subscribe((_scroll, _reduced, time) => {
    if (engine?.isScrolling === 'smooth' && document.querySelector('dialog[open]')) settle();
    engine?.raf(time);
    return engine?.isScrolling === 'smooth';
  });
  if (pending) {
    // The wheel that woke a touch-first device is buffered too, not dropped or jumped.
    engine.scrollTo(engine.targetScroll + pending * feel.wheel);
    scrollMotion.schedule();
  }
  pending = 0;
}
// A touch-first device holds its first wheel distance while the engine loads.
document.addEventListener(
  'wheel',
  (event) => {
    if (
      engine ||
      unavailable ||
      document.hidden ||
      reduced.matches ||
      desktop.matches ||
      !ownsWheel(event, event.deltaX, event.deltaY)
    )
      return;
    event.preventDefault();
    pending += event.deltaY * (event.deltaMode === 1 ? 100 / 6 : event.deltaMode === 2 ? innerHeight : 1);
    wheelSeen = true;
    // Retry whenever no build is in flight, so held distance is never left waiting.
    if (!building) sync();
  },
  { passive: false },
);
reduced.addEventListener('change', sync);
desktop.addEventListener('change', sync);
document.addEventListener('visibilitychange', sync);
window.addEventListener('pagehide', () => {
  generation++;
  settle();
  unsubscribe?.();
  engine?.destroy();
  engine = null;
});
// pageshow also fires on the first load; only a page restored from the back/forward cache needs a rebuild.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) sync();
});
document.addEventListener('pointerdown', settle, { passive: true, capture: true });
document.addEventListener('keydown', settle, { capture: true });
sync();
